<script setup lang="ts">
import WidgetSidebar from "@/components/widget/WidgetSidebar.vue";
import WidgetHeader from "@/components/widget/WidgetHeader.vue";
import { useUserStore } from "@/features/users/user.store";
import { onMounted, onUnmounted, ref, watch } from "vue";
import { useNotificationStore } from "@/features/notification/notification.store";
import { useNotification } from "@/utils/notification";
import {
  AlertCircle,
  Building07,
  X,
  File06,
  MessageTextSquare02,
  Shield02,
} from "utruckin-icons";
import { getToken } from "@/utils/token";
import { useLiveStatsStore } from "@/features/live-stats/live-stats.store";
import router from "@/router";

const userStore = useUserStore();
const notificationStore = useNotificationStore();
const liveStatsStore = useLiveStatsStore();

const { pushNotification } = useNotification();

const loading = ref(true);
const notificationLoading = ref(false);
let intervalId: number;

const getList = async () => {
  if (notificationLoading.value) return;
  await notificationStore.getCount(notificationLoading, (data: any) => {
    notificationStore.notificationCount = data.count;
  });
};

const notifications = ref<any[]>([]);
let socket: WebSocket | null = null;
let liveStatsSocket: WebSocket | null = null;

const API_BASE_URL = process.env.VUE_APP_API_BASE_URL as string;

const deleteNotification = (id: string) => {
  notifications.value = notifications.value.filter((item) => item.id !== id);
};

const pushNot = (notification: any) => {
  if (notifications.value.length >= 3) {
    notifications.value.splice(0, 1);
  }

  notifications.value.push(notification);
};

const connectWebSocket = () => {
  const token = localStorage.getItem("access_token");
  if (!token) return;

  const wsUrl = `wss://api.board.eld.center/notifications/ws?token=${token}`;

  socket = new WebSocket(wsUrl);

  socket.onmessage = (event) => {
    const data = JSON.parse(event.data);

    pushNot(data.data);
  };

  socket.onerror = (error: any) => {
    const message = error instanceof Error ? error.message : String(error);
    pushNotification(message, "error");
  };

  socket.onclose = () => {
    setTimeout(() => {
      connectWebSocket();
    }, 5000);
  };
};

const connectLiveStatsWebSocket = () => {
  const token = localStorage.getItem("access_token");
  if (!token) return;

  const organizationId = localStorage.getItem("organizationId");

  if (!organizationId) router.push("/organization");

  liveStatsSocket = new WebSocket(
    `wss://api.board.eld.center/live-board-stats/ws?token=${token}&organization_id=${organizationId}`
  );

  liveStatsSocket.onmessage = (event) => {
    const data = JSON.parse(event.data);
    liveStatsStore.liveStatsData = data.data;
  };

  liveStatsSocket.onerror = (error: any) => {
    const message = error instanceof Error ? error.message : String(error);
    pushNotification(message, "error");
  };

  liveStatsSocket.onclose = () => {
    setTimeout(() => connectWebSocket(), 5000);
  };
};

const checkOrganizationId = () => {
  if (!localStorage.getItem("organizationId")) router.push("/organization");
};

watch(
  () => localStorage.getItem("organizationId"),
  () => {
    if (!localStorage.getItem("organizationId")) router.push("/organization");
  }
);

onMounted(() => {
  userStore.getUserProfile(loading);
  checkOrganizationId();
  getList();

  intervalId = window.setInterval(() => {
    getList();
  }, 60 * 1000);

  connectWebSocket();

  connectLiveStatsWebSocket();
});

onUnmounted(() => {
  clearInterval(intervalId);
  socket?.close();
  liveStatsSocket?.close();
});
</script>

<template>
  <div v-if="!loading" class="l-main">
    <div class="sidebar">
      <WidgetSidebar />
    </div>
    <div class="home">
      <WidgetHeader />
      <router-view />
    </div>

    <div class="c-cu-alert-container">
      <TransitionGroup name="toast">
        <div
          v-for="(item, index) in notifications"
          :key="item.id"
          class="c-cu-alert"
          :style="{
            bottom: `${index * 12}px`,
            right: '12px',
            zIndex: 100 + index,
          }"
        >
          <div class="icon" :class="item.type">
            <File06 v-if="item.type == 'task_assigned'" :size="20" />
            <AlertCircle v-else-if="item.type == 'qa_failed'" :size="20" />
            <MessageTextSquare02
              v-else-if="item.type == 'task_comment_added'"
              :size="20"
            />
            <Building07
              v-else-if="item.type == 'company_created_without_team'"
              :size="20"
            />
            <Shield02 v-else :size="20" />
          </div>
          <div class="body">
            <div class="body__header">
              <div class="headline">
                <div class="body__header--title">{{ item.title }}</div>
              </div>
              <div
                class="body__header--time"
                @click="deleteNotification(item.id)"
              >
                <X :size="20" />
              </div>
            </div>

            <div class="body__main">
              {{ item.body }}
            </div>
          </div>
        </div>
      </TransitionGroup>
    </div>
  </div>
</template>

<style scoped lang="scss">
.l-main {
  display: flex;
  background-color: var(--color-secondary-system-background);
}

.home {
  width: 100%;
  overflow-y: auto;
}

.c-cu-alert-container {
  position: fixed;
  right: 16px;
  bottom: 16px;
  z-index: 99999;
}

.c-cu-alert {
  position: absolute;
  min-width: 550px;
  max-width: 550px;
  background: var(--color-system-background);
  border-radius: 20px;
  box-shadow: 0px 8px 20px 0px #49494940;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 1000;
  display: flex;
  padding: 12px;
  gap: 12px;

  .icon {
    background: var(--color-system-background);
    border: 1px solid var(--color-blue-light);
    border-radius: 100px;
    min-width: 44px;
    height: 44px;
    display: flex;
    justify-content: center;
    align-items: center;
    color: var(--color-primary);

    &.task_assigned {
      color: var(--color-system-green);
      border-color: var(--color-green-light);
    }

    &.qa_failed {
      color: var(--color-system-red);
      border-color: var(--color-red-light);
    }
  }

  .body {
    flex: 1;

    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 4px;

      .headline {
        display: flex;
        align-items: center;
      }

      &--title {
        font-size: 20px;
        line-height: 28px;
        font-weight: 500;
        color: var(--color-label);
      }

      &--time {
        font-size: 12px;
        line-height: 20px;
        color: var(--color-label-secondary);
      }
    }

    &__main {
      font-size: 16px;
      line-height: 24px;
      font-weight: 400;
      color: var(--color-label-secondary);
    }
  }
}

/* ENTER ANIMATION */
.toast-enter-from {
  opacity: 0;
  transform: translateY(60px);
}

.toast-enter-active {
  transition: all 0.35s cubic-bezier(0.22, 1, 0.36, 1);
}

.toast-enter-to {
  opacity: 1;
  transform: translateY(0);
}

/* LEAVE ANIMATION */
.toast-leave-active {
  transition: opacity 0.4s ease;
}

.toast-leave-from {
  opacity: 1;
}

.toast-leave-to {
  opacity: 0;
}
</style>
